import { useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    Checkbox,
    Divider,
    IconButton,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add";
import {
    Form,
    FormikProvider,
    useFormik
} from "formik";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { GooglePlacesAutocomplete } from "../../address-autocomplete";

import { useGenericQuery } from "../../../services/react-query/generic.query";
import { SystemUserTypes } from "../../../types";
import { CreateGroupParams } from "../../../types/groups.types";
import { createGroupParams } from "../../../schemas/group.schema";
import { handleApiError } from "../../../services/react-query/react-query.client";
import { DeleteForever } from "@mui/icons-material";
import NoData from "../../no-data";
import { GroupService } from "../../../services/groups.service";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import TeamCard from "../../../pages/app/groups-and-teams/team-card";
import TagsSelection from "../team-form/tags-selection";
import { Tag } from "../../../types/tags.types";


const styles = {
    selectContainer: {
        marginTop: '16px',
        borderRadius: 1,
        border: '0.3px solid',
        overflow: 'auto',
        p: 1,
    },
    selectItemName: {
        fontSize: '16px',
        color: '#919EAB',
        fontWeight: '400',
        fontFamily: 'Poppins, sans-serif',
        ml: 1.5,
    },
    selectItemContainer: {
        p: 1,
        ':hover': {
            background: '#F5F6F8',
            cursor: 'pointer',
        },
        maxHeight: '60px',
        height: '60px',
    }
}

export const GroupForm: React.FC = () => {
    const theme = useTheme();
    const colorButton = theme.palette.primary.main
    const navigate = useNavigate();
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [leadersErrored, setLeadersErrored] = useState<boolean>(false);
    const {
        selectedGroup,
        loading,
    } = useSelector((state: RootState) => state.groups);
    const {
        data: teamLeaders,
        isLoading: isTeamLeadersLoading,
    } = useGenericQuery<SystemUserTypes.Model[]>(
        'fetchGroupLeaders',
        'customerWebAppUser/getGroupLeaders',
        undefined,
        undefined,
        false,
    );

    const [selectedTeamLeaders, setSelectedTeamLeaders] = useState<SystemUserTypes.Model[]>([]);
    const {
        items: availableTags,
    } = useSelector((state: RootState) => state.tags);
    const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

    const formik = useFormik<CreateGroupParams>({
        initialValues: {
            name: selectedGroup?.name ?? "",
            location: selectedGroup?.location ?? "",
            leadersIds: selectedGroup?.leadersIds ?? [],
            tags: selectedGroup?.tags ?? [],
            settings: selectedGroup?.settings ?? {
                checkOnMeEnabled: false,
                defaultContacts: [{ full_name: '', phone_number: '+1' }, { full_name: '', phone_number: '+1' }],
                calenderEnabled: false
            }
        } as CreateGroupParams,
        enableReinitialize: true,
        validationSchema: createGroupParams,
        onSubmit: async (values) => {
            if (!selectedTeamLeaders) {
                setSubmitError("Please select at least one team leader to create a team.");
                setLeadersErrored(true);
                return;
            }

            const groupData: CreateGroupParams = {
                name: values.name!,
                location: values.location,
                leadersIds: selectedTeamLeaders?.map((t) => t.id)!,
                tags: selectedTags.map(t => t._id!),
                settings: values.settings,
            };

            if (!selectedGroup) {
                const createdGroup = await GroupService.createGroup(groupData, (isValid) => {
                    if (!isValid) {
                        const msg = handleApiError('Error creating group');
                        toast(msg, {
                            autoClose: 1500,
                            type: "error",
                        });
                    } else {
                        toast("Group created successfully", {
                            autoClose: 1500,
                            type: "success",
                        });
                    }
                });
                
                // Navigate to edit page after successful creation
                if (createdGroup) {
                    navigate(`/groups/${createdGroup.id}/edit`);
                }
            } else {
                await GroupService.updateGroup(selectedGroup, groupData, (isValid) => {
                    if (!isValid) {
                        const msg = handleApiError('Error updating group');
                        toast(msg, {
                            autoClose: 1500,
                            type: "error",
                        });
                    } else {
                        toast("Group Updated successfully", {
                            autoClose: 1500,
                            type: "success",
                        });
                        // Clear selected group and navigate
                        GroupService.setGroup(null);
                        setTimeout(() => {
                            navigate('/groups-and-teams');
                        }, 100);
                    }
                });
            }
        },
    });

    const { handleSubmit, isSubmitting, errors, touched, getFieldProps, setFieldValue } = formik;

    const selectTeamLeader = (teamLeader: SystemUserTypes.Model) => {
        if (teamLeaderIsSelected(teamLeader.id)) {
            setSelectedTeamLeaders(selectedTeamLeaders.filter((t) => t.id !== teamLeader.id));
        } else {
            setSelectedTeamLeaders([...selectedTeamLeaders, teamLeader]);
        }
    }

    const teamLeaderIsSelected = (leaderId: string) => {
        return selectedTeamLeaders.filter(t => t.id === leaderId).length > 0;
    }

    useEffect(() => {
        if (selectedGroup && teamLeaders) {
            // Edit mode: populate form with existing group data
            setFieldValue('name', selectedGroup.name);
            setFieldValue('location', selectedGroup.location);
            const selectedLeaders = teamLeaders.filter((leader) => selectedGroup.leadersIds?.includes(leader.id));
            setSelectedTeamLeaders(selectedLeaders);
            
            // Set tags for edit mode
            const groupTags = availableTags.filter((t: Tag) => (selectedGroup?.tags ?? []).includes(t._id!));
            setSelectedTags(groupTags);
        } else {
            // Create mode: clear all form state
            setSelectedTeamLeaders([]);
            setSelectedTags([]);
            setSubmitError(null);
            setLeadersErrored(false);
        }
    }, [selectedGroup, teamLeaders, availableTags, setFieldValue]);

    const handleTagSelectionChange = (tags: Tag[]) => {
        setSelectedTags(tags)
    };

    const submitDisabled = Object.values(errors || {})?.length > 0 ||
        selectedTeamLeaders?.length === 0;

    const width = '40vw'
    return (
        <>
            <Box
                sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    overflowY: 'scroll'
                }}
            >
                <FormikProvider value={formik}>
                    <Form noValidate onSubmit={handleSubmit}>
                        <Box
                            display='flex'
                            flexDirection='row'
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "start",
                                    alignItems: "center",
                                    flexDirection: "column",
                                }}
                            >
                                {/* Group Name */}
                                <Typography fontSize={16} variant="body1" fontWeight={500} color={'text.default'}>
                                    Group Name
                                </Typography>
                                <Box mb={3}>
                                    <Box
                                        sx={{
                                            width: width,
                                            marginTop: 1
                                        }}>
                                        <TextField
                                            sx={{
                                                marginTop: 1
                                            }}
                                            fullWidth
                                            label="Group Name"
                                            error={Boolean(errors.name) && touched.name}
                                            helperText={errors.name}
                                            {...getFieldProps('name')}
                                        />
                                    </Box>
                                </Box>
                                {/* Location */}
                                <Typography fontSize={16} variant="body1" fontWeight={500} color={'text.default'}>
                                    Location
                                </Typography>
                                <Box mb={3}>
                                    <Box
                                        sx={{
                                            width: width,
                                            marginTop: 1
                                        }}>
                                        <GooglePlacesAutocomplete
                                            key={`location-${formik.values.location || 'empty'}`}
                                            initialValue={formik.values.location ? {
                                                description: formik.values.location,
                                                place_id: ""
                                            } : undefined}
                                            onAddressSelect={(address: string, placeId: string) => {
                                                setFieldValue('location', address);
                                            }}
                                        />
                                    </Box>
                                </Box>
                                {/* Tag Selection */}
                                <TagsSelection onSelectedTagsChange={handleTagSelectionChange} selectedTags={selectedTags} />
                                {/* TEAM Leaders */}
                                <Typography fontSize={16} variant="body1" mt={4} fontWeight={500} color={'text.default'}>
                                    Group Leaders ({selectedTeamLeaders?.length} selected) <Typography color="error" component="span">*</Typography>
                                </Typography>

                                {/* TEAM Leaders */}
                                <Box mb={3}>
                                    <Box
                                        sx={{
                                            width: width,
                                            marginTop: 1
                                        }}>
                                        <Autocomplete
                                            disablePortal
                                            sx={{ width: '100%' }}
                                            renderInput={(params) =>
                                                <TextField
                                                    {...params}
                                                    sx={{ marginTop: '16px' }}
                                                    fullWidth
                                                    label="Search for group leaders"
                                                />
                                            }
                                            getOptionLabel={(option) => option?.fullname || option?.email || option?.phone}
                                            onChange={(e, value) => {
                                                return undefined
                                            }}
                                            options={teamLeaders || []}
                                            renderOption={(_, option) => !teamLeaderIsSelected(option?.id) && (
                                                <Box
                                                    key={option?.id}
                                                    display="flex"
                                                    flexDirection="row"
                                                    alignItems="center"
                                                    justifyContent="flex-start"
                                                    sx={{
                                                        ...styles.selectItemContainer,
                                                        borderRadius: 1,
                                                        background: "white",
                                                        cursor: "pointer",
                                                        padding: "8px",
                                                    }}
                                                    onClick={() => {
                                                        selectTeamLeader(option);
                                                    }}
                                                >
                                                    <Avatar
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            fontSize: 14,
                                                            bgcolor: '#e3f2fd',
                                                            color: '#1976d2',
                                                            mr: 2,
                                                        }}
                                                    >
                                                        {option.fullname?.[0] || ''}
                                                    </Avatar>
                                                    <Typography sx={styles.selectItemName}>{option?.fullname}</Typography>
                                                </Box>

                                            )}
                                        />
                                    </Box>
                                </Box>

                                {/* TEAM Leaders */}
                                <Box
                                    mb={3}
                                    display="flex"
                                    flexWrap="wrap"
                                    alignItems="center"
                                    alignContent="flex-start"
                                    justifyContent="flex-start"
                                    sx={{
                                        ...styles.selectContainer,
                                        width: width,
                                        minHeight: '120px',
                                        maxHeight: 'none',
                                        borderColor: leadersErrored ? 'red' : '#ccc',
                                        overflow: 'visible',
                                        gap: 1,
                                        padding: '8px',
                                    }}
                                >
                                    {selectedTeamLeaders?.length === 0 && (
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            textAlign="center"
                                            width="100%"
                                        >
                                            <NoData
                                                title="No selected leaders"
                                                description="Select group leaders to add to the team"
                                                buttonText={null}
                                                onClick={() => { }}
                                            />
                                        </Box>
                                    )}

                                    {selectedTeamLeaders?.map((teamLeader) => (
                                        <Box
                                            key={teamLeader?.id}
                                            display="flex"
                                            flexDirection="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{
                                                ...styles.selectItemContainer,
                                                borderRadius: 2,
                                                backgroundColor: 'white',
                                                padding: '8px 12px',
                                                boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
                                                width: 'auto',
                                                minWidth: '180px',
                                                maxWidth: 'auto',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {/* Avatar + Name Container */}
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Avatar
                                                    sx={{
                                                        width: 28,
                                                        height: 28,
                                                        fontSize: 14,
                                                        bgcolor: '#e3f2fd',
                                                        color: '#1976d2',
                                                        mr: 2,
                                                    }}
                                                >
                                                    {teamLeader.fullname?.[0] || ''}
                                                </Avatar>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        color: '#424242',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {teamLeader?.fullname}
                                                </Typography>
                                            </Box>
                                            <Tooltip title="Remove group leader">
                                                <IconButton
                                                    sx={{
                                                        color: '#757575',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            color: '#d32f2f',
                                                        },
                                                    }}
                                                    onClick={() => {
                                                        setSelectedTeamLeaders(selectedTeamLeaders.filter((t) => t.id !== teamLeader.id));
                                                    }}
                                                >
                                                    <DeleteForever />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{
                                    mx: 2,
                                    borderWidth: "2px",
                                    borderColor: "rgba(0, 0, 0, 0.2)",
                                }}
                            />
                            <Box
                                display='flex'
                                flexDirection='column'
                                width='30vw'
                                justifyContent="space-between"
                            >
                                {/* Group Protocols */}
                                <Box>
                                    <Typography
                                        fontSize={16}
                                        variant="h4"
                                        fontWeight={500}
                                        color={'text.default'}
                                        sx={{
                                            mb: 2,
                                        }}
                                    >
                                        Group Protocols
                                    </Typography>
                                    {/* Check On Me */}
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            backgroundColor: "#f7f7f7",
                                        }}
                                    >
                                        <Stack direction="row" alignItems="flex-start" spacing={2}>
                                            <Checkbox
                                                checked={formik.values.settings?.checkOnMeEnabled ?? false}
                                                onChange={(e) => {
                                                    console.log(formik.values)
                                                    formik.setFieldValue('settings', { ...formik.values.settings ?? {}, checkOnMeEnabled: e.target.checked })
                                                }}
                                                sx={{ mt: 0.5 }}
                                            />
                                            <Box>
                                                <Typography fontWeight={600}>Check On Me</Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Enable ‘Check on Me’ feature in the mobile app for this team
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>

                                </Box>
                            </Box>
                        </Box>
                        {/* Add Teams Area */}
                        {
                            selectedGroup ? (
                                <Box
                                    mt={5}
                                >
                                    <Typography
                                        fontSize={16}
                                        variant="body1"
                                        fontWeight={500}
                                        color={'text.default'}
                                        mr={5}
                                    >
                                        Add Teams to {selectedGroup?.name} group
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        sx={{
                                            fontSize: "12px",
                                            padding: "4px 8px",
                                            minWidth: "auto",
                                            alignItems: "center",
                                            mt: 2
                                        }}
                                        onClick={() => {
                                            GroupService.setGroup(selectedGroup)
                                            navigate(`/teams/create?source=group&groupId=${selectedGroup.id}`)
                                        }}
                                        startIcon={
                                            <Avatar
                                                sx={{
                                                    bgcolor: "white",
                                                    color: colorButton,
                                                    width: 18,
                                                    height: 18,
                                                    fontSize: "10px",
                                                }}
                                            >
                                                <AddIcon fontSize="inherit" />
                                            </Avatar>
                                        }
                                    >
                                        Add Team
                                    </Button>
                                    {selectedGroup.teams?.map((team, idx) => (
                                        <TeamCard key={team.id || idx} team={team} />
                                    ))}
                                </Box>) : <Box></Box>
                        }
                        {/* Cancel and Save Button */}
                        <Box
                            display="flex"
                            flexDirection="row-reverse"
                            gap={2}
                            mt={4}
                        >
                            <LoadingButton
                                variant="contained"
                                sx={{ ml: 1 }}
                                size="medium"
                                onClick={async () => {
                                    handleSubmit();
                                }}
                                disabled={submitDisabled}
                                loading={loading['createGroup'] || loading['updateGroup']}
                            >
                                Save
                            </LoadingButton>

                            <LoadingButton
                                variant="outlined"
                                color="inherit"
                                size="medium"
                                onClick={() => {
                                    GroupService.setGroup(null);
                                    navigate('/groups-and-teams');
                                }}
                                disabled={isSubmitting}
                                loading={false}
                            >
                                Cancel
                            </LoadingButton>
                        </Box>
                    </Form>
                </FormikProvider>
            </Box >

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={submitError !== null}
                sx={{ mt: 4 }}
                color={'red'}
                onClose={() => {
                    setSubmitError(null);
                }}
                message={submitError}
            />
        </>
    )
}